import { Injectable, inject, signal } from '@angular/core';

import { NotificationsHubService } from './notifications-hub.service';

export type ConnectionStatus = 'online' | 'reconnecting' | 'offline';

/** Agent Workspace phase's Reconnect Handling / Offline Detection / Connection Recovery
 * (Streams 1 &amp; 8). Two independent signals feed into one status: the browser's own
 * navigator.onLine/online/offline events (network-level), and NotificationsHubService's
 * reconnecting/reconnected/closed events (SignalR-level, via automatic reconnect) -- either
 * one being bad is enough to show the banner, and both must be good to clear it. There is
 * no bespoke heartbeat endpoint; this relies entirely on SignalR's own built-in keep-alive
 * and automatic reconnect (see the implementation report's Real-Time Experience section for
 * why a custom heartbeat would just duplicate that). */
@Injectable({ providedIn: 'root' })
export class ConnectionStatusService {
  private readonly notificationsHub = inject(NotificationsHubService);

  private readonly browserOnline = signal(navigator.onLine);
  private readonly hubReconnecting = signal(false);

  readonly status = signal<ConnectionStatus>('online');

  constructor() {
    window.addEventListener('online', () => {
      this.browserOnline.set(true);
      this.recompute();
    });
    window.addEventListener('offline', () => {
      this.browserOnline.set(false);
      this.recompute();
    });

    this.notificationsHub.reconnecting$.subscribe(() => {
      this.hubReconnecting.set(true);
      this.recompute();
    });
    this.notificationsHub.reconnected$.subscribe(() => {
      this.hubReconnecting.set(false);
      this.recompute();
    });
    this.notificationsHub.closed$.subscribe(() => {
      this.hubReconnecting.set(true);
      this.recompute();
    });
  }

  private recompute(): void {
    if (!this.browserOnline()) {
      this.status.set('offline');
    } else if (this.hubReconnecting()) {
      this.status.set('reconnecting');
    } else {
      this.status.set('online');
    }
  }
}
